<template>
	<div class="admin-table-section">
		<!-- Section Header -->
		<div
			class="section-header"
			@click="collapsible && toggleExpanded()"
			:style="{ cursor: collapsible ? 'pointer' : 'default' }"
		>
			<h2>
				<span v-if="icon" class="pin-icon">{{ icon }}</span>
				{{ title }}
				<span v-if="collapsible" class="expand-icon" :class="{ rotated: isExpanded }">›</span>
				<span v-if="badge" class="filter-badge">{{ badge }}</span>
			</h2>
			<div v-if="searchable" class="search-controls" @click.stop>
				<input
					v-model="internalSearchQuery"
					type="text"
					:placeholder="searchPlaceholder"
					class="search-input"
				/>
				<button
					v-if="showClearButton || searchQuery"
					class="reset-filter-btn"
					@click="clearSearch"
					title="Clear filter"
				>
					Clear Filter ✕
				</button>
			</div>
		</div>

		<!-- Table Content -->
		<transition name="expand">
			<div v-if="!collapsible || isExpanded" class="table-wrapper">
				<!-- Loading State -->
				<div v-if="loading" class="list-loading">
					<v-skeleton-loader type="table" :loading="loading"/>
				</div>

				<!-- Table -->
				<div v-else class="table-content">
					<!-- Table Header -->
					<div class="table-header" :style="{ gridTemplateColumns: gridTemplateColumns }">
						<div
							v-for="(column, index) in localColumns"
							:key="column.field"
							:class="['header-cell', { sortable: column.sortable }]"
							@click="column.sortable && handleSort(column.field)"
							:title="column.sortable ? 'Click to sort' : ''"
						>
							{{ column.label }}
							<span v-if="column.sortable && sortByField === column.field" class="sort-indicator">
								{{ sortOrderState === 'asc' ? ' ↑' : ' ↓' }}
							</span>
							<div class="resizer" @mousedown.stop="startResize($event, index)"></div>
						</div>
						<div v-if="hasActions" class="header-cell">Actions</div>
					</div>

					<!-- Data Rows -->
					<div v-if="filteredSortedData.length > 0">
						<div
							v-for="(row, index) in filteredSortedData"
							:key="row.id || index"
							class="table-row"
							:class="{
								'selected': selectable && selectedRowId === row.id,
								'editing': editable && editingRowId === row.id
							}"
							@click="handleRowClick(row)"
							:style="{
								gridTemplateColumns: gridTemplateColumns,
								cursor: (selectable || editable) ? 'pointer' : 'default'
							}"
						>
							<!-- Data Cells -->
							<div
								v-for="column in localColumns"
								:key="column.field"
								:class="`col-${column.field}`"
								:data-label="column.label"
							>
								<!-- Editing Mode -->
								<template v-if="editable && editingRowId === row.id && column.editable">
									<textarea
										v-if="column.type === 'textarea'"
										v-model="editingData[column.field]"
										:placeholder="column.placeholder"
										class="cell-input"
										:disabled="savingRowId === row.id"
										@click.stop
									/>
									<input
										v-else
										v-model="editingData[column.field]"
										:type="getInputType(column.type)"
										:placeholder="column.placeholder"
										class="cell-input"
										:disabled="savingRowId === row.id"
										@click.stop
									/>
								</template>

								<!-- View Mode or Custom Slot -->
								<template v-else>
									<slot :name="`cell-${column.field}`" :row="row" :value="row[column.field]" :editingData="editingData" :editingRowId="editingRowId">
										<span class="cell-value">{{ formatCellValue(row[column.field], column.type) }}</span>
									</slot>
								</template>
							</div>

							<!-- Action Buttons -->
							<div v-if="hasActions" class="col-actions">
								<!-- Editing Mode Actions -->
								<template v-if="editable && editingRowId === row.id">
								<template v-if="savingRowId === row.id">
									<div class="action-btn-spinner">
										<v-progress-circular indeterminate size="20" width="2"></v-progress-circular>
									</div>
								</template>
								<button v-else class="action-btn save" @click.stop="saveEdit" title="Save">💾</button>

									<button class="action-btn cancel" @click.stop="cancelEdit" title="Cancel" :disabled="savingRowId === row.id">✕</button>
								</template>

								<!-- View Mode Actions -->
								<template v-else>
									<button v-if="editable" class="action-btn edit" @click.stop="startEdit(row)" title="Edit" :disabled="deletingRowId === row.id">✏️</button>
									<slot name="custom-actions" :row="row"></slot>
									<button v-if="deletable && deletingRowId !== row.id" class="action-btn delete" @click.stop="handleDelete(row)" title="Delete">🗑️</button>
									<div v-if="deletingRowId === row.id" class="action-btn-spinner">
										<v-progress-circular indeterminate size="20" width="2"></v-progress-circular>
									</div>
								</template>
							</div>
						</div>
					</div>

					<!-- Empty State -->
					<div v-else class="table-empty">
						<p>{{ emptyMessage }}</p>
					</div>

					<!-- Add New Row -->
					<div v-if="addable" class="table-row add-row" :style="{ gridTemplateColumns: gridTemplateColumns }">
						<div
							v-for="column in localColumns"
							:key="`add-${column.field}`"
							:class="`col-${column.field}`"
							:data-label="column.label"
						>
							<textarea
								v-if="column.type === 'textarea'"
								v-model="addRowData[column.field]"
								:placeholder="column.placeholder"
								class="cell-input"
								:readonly="!column.editable"
								:disabled="addingRow"
							/>
							<input
								v-else-if="column.editable"
								v-model="addRowData[column.field]"
								:type="getInputType(column.type)"
								:placeholder="column.placeholder"
								class="cell-input"
								:disabled="addingRow"
							/>
							<slot v-else-if="$slots[`add-cell-${column.field}`]" :name="`add-cell-${column.field}`" :addRowData="addRowData"></slot>
							<span v-else class="cell-value">{{ column.addRowDefault || '—' }}</span>
						</div>

						<!-- Add Row Actions -->
						<div v-if="hasActions" class="col-actions">
							<template v-if="isAddRowDirty">
								<template v-if="addingRow">
									<div class="action-btn-spinner">
										<v-progress-circular indeterminate size="20" width="2"></v-progress-circular>
									</div>
								</template>
								<button v-else-if="isAddRowValid" class="action-btn save" @click="submitAddRow" title="Save">💾</button>

								<button class="action-btn cancel" @click="resetAddRow" title="Cancel" :disabled="addingRow">✕</button>
							</template>
						</div>
					</div>
				</div>
			</div>
		</transition>
	</div>
</template>

<script>
export default {
	name: 'AdminTable',
	props: {
		title: {
			type: String,
			required: true
		},
		icon: {
			type: String,
			default: ''
		},
		columns: {
			type: Array,
			required: true,
			// Example: [
			//   {
			//     field: 'name',
			//     label: 'Name',
			//     width: '2fr',
			//     sortable: true,
			//     editable: true,
			//     type: 'text',
			//     required: true,
			//     placeholder: 'Enter name...',
			//     addRowDefault: '—'
			//   }
			// ]
		},
		data: {
			type: Array,
			default: () => []
		},
		loading: {
			type: Boolean,
			default: false
		},
		searchable: {
			type: Boolean,
			default: false
		},
		searchPlaceholder: {
			type: String,
			default: 'Search...'
		},
		showClearButton: {
			type: Boolean,
			default: false
		},
		collapsible: {
			type: Boolean,
			default: false
		},
		defaultExpanded: {
			type: Boolean,
			default: true
		},
		editable: {
			type: Boolean,
			default: false
		},
		selectable: {
			type: Boolean,
			default: false
		},
		deletable: {
			type: Boolean,
			default: false
		},
		addable: {
			type: Boolean,
			default: false
		},
		emptyMessage: {
			type: String,
			default: 'No data available'
		},
		badge: {
			type: [String, Number],
			default: null
		},
		externalSearchQuery: {
			type: String,
			default: ''
		},
	savingRowId: {
		type: [String, Number],
		default: null
	},
	addingRow: {
		type: Boolean,
		default: false
	},
	deletingRowId: {
		type: [String, Number],
		default: null
	}
	},
	emits: [
		'row-select',
		'row-edit-start',
		'row-edit-save',
		'row-edit-cancel',
		'row-delete',
		'row-add',
		'row-action',
		'search-update',
		'search-clear'
	],
	data() {
		return {
			isExpanded: this.defaultExpanded,
			internalSearchQuery: '',
			sortByField: null,
			sortOrderState: 'asc',
			editingRowId: null,
			editingData: {},
			selectedRowId: null,
			addRowData: {},
			addRowInitial: {},
			localColumns: [],
			resizingColumnIndex: null,
			startX: 0
		};
	},
	created() {
		// Create a deep copy of the columns to make them mutable
		this.localColumns = JSON.parse(JSON.stringify(this.columns));
	},
	computed: {
		gridTemplateColumns() {
			const columnWidths = this.localColumns.map(col => col.width || '1fr').join(' ');
			return this.hasActions ? `${columnWidths} auto` : columnWidths;
		},
		searchQuery() {
			return this.externalSearchQuery || this.internalSearchQuery;
		},
		filteredSortedData() {
			let result = [...this.data];

			// Apply search filter
			if (this.searchQuery.trim()) {
				const query = this.searchQuery.toLowerCase();
				result = result.filter(row => {
					return this.columns.some(col => {
						const value = row[col.field];
						if (value === null || value === undefined) return false;
						return String(value).toLowerCase().includes(query);
					});
				});
			}

			// Apply sorting
			if (this.sortByField) {
				result.sort((a, b) => {
					const column = this.columns.find(col => col.field === this.sortByField);
					let aVal = a[this.sortByField];
					let bVal = b[this.sortByField];

					// Handle date type
					if (column?.type === 'date') {
						aVal = new Date(aVal || 0).getTime();
						bVal = new Date(bVal || 0).getTime();
					} else {
						// Handle text/number
						aVal = (aVal || '').toString().toLowerCase();
						bVal = (bVal || '').toString().toLowerCase();
					}

					if (aVal < bVal) return this.sortOrderState === 'asc' ? -1 : 1;
					if (aVal > bVal) return this.sortOrderState === 'asc' ? 1 : -1;
					return 0;
				});
			}

			return result;
		},
		isAddRowDirty() {
			return Object.keys(this.addRowData).some(
				key => this.addRowData[key] !== this.addRowInitial[key]
			);
		},
		isAddRowValid() {
			return this.columns
				.filter(col => col.required)
				.every(col => this.addRowData[col.field]);
		},
		hasActions() {
			return this.editable || this.deletable || this.addable || !!this.$slots['custom-actions'];
		}
	},
	watch: {
		internalSearchQuery(val) {
			this.$emit('search-update', val);
		},
	}, // Added missing comma
	mounted() {
		this.initAddRow();
	},
	methods: {
		startResize(event, columnIndex) {
			this.resizingColumnIndex = columnIndex;
			this.startX = event.clientX;
			this.startWidth = this.$el.querySelectorAll('.header-cell')[columnIndex].offsetWidth;

			document.addEventListener('mousemove', this.doResize);
			document.addEventListener('mouseup', this.stopResize);
		},

		doResize(event) {
			if (this.resizingColumnIndex === null) return;

			const dx = event.clientX - this.startX;
			const newWidth = this.startWidth + dx;
			
			// Ensure newWidth is not smaller than a minimum width (e.g., 50px)
			if (newWidth > 50) {
				this.localColumns[this.resizingColumnIndex].width = `${newWidth}px`;
			}
		},

		stopResize() {
			this.resizingColumnIndex = null;
			document.removeEventListener('mousemove', this.doResize);
			document.removeEventListener('mouseup', this.stopResize);
		},

		toggleExpanded() {
			this.isExpanded = !this.isExpanded;
		},
		clearSearch() {
			this.internalSearchQuery = '';
			this.$emit('search-clear');
		},
		handleSort(field) {
			if (this.sortByField === field) {
				this.sortOrderState = this.sortOrderState === 'asc' ? 'desc' : 'asc';
			} else {
				this.sortByField = field;
				this.sortOrderState = 'asc';
			}
		},
		handleRowClick(row) {
			if (!this.editingRowId && this.selectable) {
				this.selectedRowId = this.selectedRowId === row.id ? null : row.id;
				this.$emit('row-select', row);
			}
		},
		startEdit(row) {
			this.editingRowId = row.id;
			this.editingData = {};
			this.columns.forEach(col => {
				this.editingData[col.field] = row[col.field] || '';
			});
			this.$emit('row-edit-start', row);
		},
		saveEdit() {
			this.$emit('row-edit-save', {
				rowId: this.editingRowId,
				data: { ...this.editingData }
			});
			// Parent is responsible for calling cancelEdit() on success
		},
		cancelEdit() {
			this.editingRowId = null;
			this.editingData = {};
			this.$emit('row-edit-cancel');
		},
		handleDelete(row) {
			this.$emit('row-delete', row);
		},
		initAddRow() {
			this.addRowData = {};
			this.addRowInitial = {};
			this.columns.forEach(col => {
				this.addRowData[col.field] = '';
				this.addRowInitial[col.field] = '';
			});
		},
		submitAddRow() {
			this.$emit('row-add', { ...this.addRowData });
			// Parent is responsible for calling resetAddRow() on success
		},
		resetAddRow() {
			this.initAddRow();
		},
		getInputType(type) {
			const typeMap = {
				'email': 'email',
				'url': 'url',
				'number': 'number',
				'date': 'date',
				'textarea': 'textarea'
			};
			return typeMap[type] || 'text';
		},
		formatCellValue(value, type) {
			if (value === null || value === undefined || value === '') return '—';

			switch (type) {
				case 'date':
					if (typeof value === 'string') {
						const date = new Date(value);
						return date.toLocaleDateString('en-GB', {
							year: 'numeric',
							month: 'short',
							day: 'numeric'
						});
					}
					return value;
				case 'number':
					return Number(value).toLocaleString();
				default:
					return value;
			}
		}
	}
};
</script>

<style scoped>
.admin-table-section {
	margin-bottom: 40px;
}

/* Section Header */
.section-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 20px;
}

.section-header h2 {
	margin: 0;
	font-size: 20px;
	color: var(--admin-text-primary);
	transition: color 0.3s ease;
	display: flex;
	align-items: center;
	gap: 8px;
}

.pin-icon {
	font-size: 20px;
}

.expand-icon {
	display: inline-block;
	font-size: 24px;
	transition: transform 0.3s ease;
	transform-origin: center;
}

.expand-icon.rotated {
	transform: rotate(90deg);
}

.filter-badge {
	font-size: 12px;
	color: var(--admin-text-secondary);
	font-weight: normal;
	margin-left: 8px;
	padding: 2px 8px;
	background-color: rgba(51, 40, 71, 0.1);
	border-radius: var(--border-radius-s);
}

.search-controls {
	display: flex;
	gap: 12px;
	align-items: center;
}

.search-input {
	padding: 8px 12px;
	background-color: var(--admin-active-bg);
	border: 1px solid var(--admin-border);
	border-radius: var(--border-radius-s);
	color: var(--admin-text-primary);
	font-size: 13px;
	font-family: inherit;
	transition: all 0.2s ease;
	width: 250px;
}

.search-input:focus {
	outline: none;
	border-color: var(--background-color-alt6);
	background-color: var(--admin-hover-bg);
	box-shadow: 0 0 0 2px rgba(198, 202, 229, 0.2);
}

.search-input::placeholder {
	color: var(--admin-text-secondary);
}

.reset-filter-btn {
	padding: 8px 12px;
	background-color: var(--background-color-primary);
	color: white;
	border: none;
	border-radius: var(--border-radius-s);
	cursor: pointer;
	font-size: 13px;
	font-family: gotham-heavy;
	transition: all 0.2s ease;
}

.reset-filter-btn:hover {
	background-color: #332847;
	transform: translateY(-1px);
	box-shadow: 0 2px 8px rgba(51, 40, 71, 0.15);
}

/* Expand/Collapse Transition */
.expand-enter-active,
.expand-leave-active {
	transition: all 0.3s ease;
}

.expand-enter-from,
.expand-leave-to {
	opacity: 0;
	max-height: 0;
}

.expand-enter-to,
.expand-leave-from {
	opacity: 1;
	max-height: 1000px;
}

/* Table Wrapper */
.table-wrapper {
	background: var(--admin-bg-secondary);
	border-radius: var(--border-radius-m);
	box-shadow: var(--box-shadow);
	overflow: hidden;
	transition: background-color 0.3s ease;
}

.table-content {
	display: flex;
	flex-direction: column;
}

.list-loading {
	padding: 20px;
}

/* Table Header */
.table-header {
	display: grid;
	gap: 20px;
	padding: 16px 20px;
	background-color: var(--admin-hover-bg);
	font-size: 12px;
	color: var(--admin-text-secondary);
	letter-spacing: 0.5px;
	border-bottom: 1px solid var(--admin-border);
	position: sticky;
	top: 0;
	z-index: 10;
	transition: all 0.3s ease;
}

.header-cell {
	display: flex;
	align-items: center;
	gap: 4px;
	transition: color 0.2s ease;
	min-width: 0;
	overflow: visible; /* To see the resizer */
	position: relative; /* For resizer positioning */
	border-right: 1px solid var(--admin-border); /* Add right border */
	box-sizing: border-box; /* Include padding and border in the element's total width and height */
}

.header-cell:last-child {
	border-right: none; /* Remove border from the last header cell */
}

.header-cell.sortable {
	cursor: pointer;
	user-select: none;
}

.header-cell.sortable:hover {
	color: var(--admin-text-primary);
}

.sort-indicator {
	font-size: 11px;
	opacity: 0.6;
	transition: opacity 0.2s ease;
}

.header-cell.sortable:hover .sort-indicator {
	opacity: 1;
}

.resizer {
	position: absolute;
	top: 0;
	right: 0;
	width: 5px;
	height: 100%;
	cursor: col-resize;
	user-select: none;
}

.resizer:hover {
	background-color: var(--background-color-primary);
}

/* Table Rows */
.table-row {
	display: grid;
	gap: 20px;
	padding: 16px 20px;
	border-bottom: 1px solid var(--admin-border);
	align-items: center;
	transition: all 0.2s ease;
}

.table-row:hover {
	background-color: var(--admin-hover-bg);
}

.table-row.selected {
	background-color: rgba(51, 40, 71, 0.1);
	border-left: 3px solid var(--background-color-primary);
}

.table-row.editing {
	background-color: rgba(51, 40, 71, 0.15);
	border-left: 3px solid var(--background-color-primary);
}

.table-row.editing .cell-input {
	background-color: var(--admin-hover-bg);
	border-color: var(--background-color-primary);
}

.table-row.add-row {
	background-color: transparent;
	border-bottom: 1px solid var(--admin-border);
}

/* Table Cells */
.table-row > div {
	display: flex;
	align-items: center;
	min-width: 0;
	overflow: hidden;
}

.cell-value {
	font-size: 13px;
	color: var(--admin-text-primary);
	line-height: 1.4;
	display: block;
	overflow: hidden;
	white-space: normal; /* Allow text to wrap */
	word-break: break-word; /* Break long words */
	transition: color 0.3s ease;
	width: 100%;
}

.cell-input {
	width: 100%;
	padding: 8px 10px;
	background-color: var(--admin-active-bg);
	border: 1px solid var(--admin-border);
	border-radius: var(--border-radius-s);
	color: var(--admin-text-primary);
	font-size: 13px;
	font-family: inherit;
	transition: all 0.2s ease;
	box-sizing: border-box;
}

.cell-input:focus {
	outline: none;
	border-color: var(--background-color-alt6);
	background-color: var(--admin-hover-bg);
	box-shadow: 0 0 0 2px rgba(198, 202, 229, 0.2);
}

textarea.cell-input {
	min-height: 80px;
	resize: vertical;
}

/* Action Buttons */
.col-actions {
	justify-content: center;
	gap: 8px;
}

.action-btn {
	background: none;
	border: none;
	cursor: pointer;
	font-size: 16px;
	padding: 6px 8px;
	border-radius: var(--border-radius-s);
	transition: all 0.2s ease;
	display: flex;
	align-items: center;
	justify-content: center;
	min-width: 32px;
	height: 32px;
}

.action-btn:hover {
	background-color: var(--admin-hover-bg);
	transform: scale(1.1);
}

.action-btn.edit {
	color: #1e88e5;
}

.action-btn.edit:hover {
	background-color: rgba(30, 136, 229, 0.1);
}

.action-btn.delete:hover {
	background-color: rgba(255, 0, 0, 0.1);
}

.action-btn.save {
	color: #9ccc65;
	filter: brightness(2.4);
}

.action-btn.save:hover {
	background-color: rgba(76, 175, 80, 0.1);
}

.action-btn.cancel {
	color: #f44336;
}

.action-btn.cancel:hover {
	background-color: rgba(244, 67, 54, 0.1);
}

.action-btn-spinner {
	display: flex;
	align-items: center;
	justify-content: center;
	min-width: 32px;
	height: 32px;
}

/* Disabled state for inputs and buttons */
.cell-input:disabled {
	opacity: 0.5;
	cursor: not-allowed;
	background-color: var(--admin-disabled-bg, #f5f5f5);
}

.action-btn:disabled {
	opacity: 0.4;
	cursor: not-allowed;
	pointer-events: none;
}

/* Empty State */
.table-empty {
	padding: 40px 20px;
	text-align: center;
	color: var(--admin-text-secondary);
	transition: color 0.3s ease;
}

.table-empty p {
	margin: 0;
	font-size: 14px;
}

/* Tablet Responsive */
@media (max-width: 768px) {
	.table-header,
	.table-row {
		gap: 10px;
		padding: 12px 15px;
	}

	.cell-value,
	.cell-input {
		font-size: 12px;
	}
}

/* Mobile Responsive - Card Layout */
@media (max-width: 480px) {
	.pin-icon {
		display: none;
	}

	.search-controls {
		flex-direction: column;
		gap: 8px;
		align-items: stretch;
	}

	.search-input {
		width: 100%;
		max-width: 100%;
	}

	.reset-filter-btn {
		width: 100%;
	}

	/* Hide table header */
	.table-header {
		display: none;
	}

	/* Card-based layout */
	.table-row {
		display: flex;
		flex-direction: column;
		gap: 0;
		padding: 16px 12px;
		margin: 0 10px 12px 10px;
		border: 1px solid var(--admin-border);
		background-color: var(--admin-hover-bg);
		border-radius: var(--border-radius-s);
		box-sizing: border-box;
		align-items: flex-start;
		text-align: left;
	}

	.table-row.add-row {
		background-color: transparent;
		border: 1px dashed var(--admin-border);
	}

	.table-row > div {
		margin-bottom: 12px;
		padding-bottom: 0;
		border-bottom: none;
		box-sizing: border-box;
		max-width: 100%;
		display: flex;
		flex-direction: column;
		align-items: flex-start !important;
		gap: 6px;
		text-align: left;
		justify-content: flex-start;
	}

	.table-row > div:last-child {
		margin-bottom: 0;
	}

	/* Field labels via pseudo-elements */
	.table-row > div:not(.col-actions)::before {
		content: attr(data-label);
		font-size: 11px;
		color: var(--admin-text-secondary);
		letter-spacing: 0.5px;
		text-transform: uppercase;
		font-family: gotham-heavy;
		text-align: left;
		width: 100%;
	}

	/* Actions column special handling */
	.col-actions {
		display: flex !important;
		flex-direction: row !important;
		gap: 10px;
		justify-content: flex-start;
		width: 100%;
		align-items: center !important;
		flex-wrap: wrap;
	}

	.col-actions::before {
		display: none;
	}

	.action-btn {
		font-size: 13px;
		padding: 8px 10px;
		min-width: auto;
		height: auto;
		gap: 6px;
		flex-shrink: 0;
	}

	.cell-value,
	.cell-input {
		width: 100%;
		font-size: 13px;
		text-align: left;
		align-self: flex-start;
	}

	.cell-input {
		box-sizing: border-box;
	}

	/* Force all content to be left-aligned on mobile */
	.table-row > div > * {
		text-align: left;
		align-self: flex-start;
	}

	/* Override any inherited centering */
	.table-row > div[class*="col-"] {
		align-items: flex-start !important;
		justify-content: flex-start !important;
		text-align: left !important;
	}
}
</style>
